import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Wallet } from "lucide-react";
import { api, errMsg, brl, fmtDateTime } from "@/lib/api";
import { useAuth, useSettings } from "@/context/AppContext";
import { useSeo } from "@/lib/draft";
import { UfSelect, maskPhone } from "@/components/AuthPanel";
import { PageHead } from "@/pages/ComoFunciona";

const STATUS = { recebido: "Cadastro recebido", aguardando_verificacao: "Aguardando verificação", aprovado: "Aprovado", suspenso: "Suspenso", rejeitado: "Rejeitado", excluido: "Excluído" };

function Register() {
  const { setUser } = useAuth();
  const [f, setF] = useState({ name: "", cpf: "", email: "", password: "", phone: "", oab_number: "", oab_section: "", oab_supplementary: "", state: "", city: "", areas: "", society_name: "", accept_terms: false });
  const up = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : k === "phone" ? maskPhone(e.target.value) : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/lawyer/register", { ...f, areas: f.areas.split(",").map((x) => x.trim()).filter(Boolean) });
      setUser(data);
      toast.success("Cadastro recebido. Aguarde a verificação.");
    } catch (e2) { toast.error(errMsg(e2)); }
  };
  const inp = (k, label, props = {}) => <div><label className="label">{label}</label><input className="field" value={f[k]} onChange={up(k)} data-testid={`lawyer-${k}-input`} {...props} /></div>;
  return (
    <form onSubmit={submit} className="card grid gap-4 sm:grid-cols-2" data-testid="lawyer-register-form">
      {inp("name", "Nome completo")}{inp("cpf", "CPF")}{inp("email", "E-mail profissional", { type: "email" })}{inp("password", "Senha", { type: "password" })}
      {inp("phone", "Celular")}{inp("oab_number", "Nº de inscrição OAB")}
      <div><label className="label">Seccional OAB</label><UfSelect value={f.oab_section} onChange={(v) => setF({ ...f, oab_section: v })} testid="lawyer-oab-section-select" /></div>
      {inp("oab_supplementary", "Inscrição suplementar (opcional)")}
      <div><label className="label">Estado de atuação</label><UfSelect value={f.state} onChange={(v) => setF({ ...f, state: v })} testid="lawyer-state-select" /></div>
      {inp("city", "Município")}{inp("areas", "Áreas de atuação (separadas por vírgula)")}{inp("society_name", "Sociedade de advocacia (opcional)")}
      <label className="flex gap-2 text-sm sm:col-span-2"><input type="checkbox" checked={f.accept_terms} onChange={up("accept_terms")} data-testid="lawyer-terms-checkbox" />Declaro a veracidade dos dados e aceito a verificação da inscrição profissional. A conta não concede acesso automático a solicitações.</label>
      <button className="btn-primary sm:col-span-2" data-testid="lawyer-register-submit">Enviar cadastro</button>
    </form>
  );
}

function Portal() {
  const [me, setMe] = useState(null);
  const [wallet, setWallet] = useState(null);
  const load = async () => {
    const { data } = await api.get("/lawyer/me");
    setMe(data);
    if (data.features.wallet_enabled && data.profile?.status === "aprovado") api.get("/lawyer/wallet").then((r) => setWallet(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const simulate = async (pkg) => {
    try {
      await api.post("/lawyer/wallet/simulate-purchase", { package_id: pkg, idempotency_key: crypto.randomUUID() });
      toast.success("Aquisição SIMULADA registrada");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!me) return null;
  const p = me.profile || {};
  return (
    <div className="space-y-6" data-testid="lawyer-portal">
      <section className="card">
        <h2 className="text-lg font-extrabold">{me.user.name}</h2>
        <p className="mt-1 text-sm">OAB {p.oab_number}/{p.oab_section} · {p.city}/{p.state}</p>
        <p className="mt-3"><span className="chip bg-[#EDF5FF] text-[#1465C8]" data-testid="lawyer-status">{STATUS[p.status]}</span></p>
        <p className="mt-3 text-sm text-[#64748B]">Especialidades: {(p.areas || []).join(", ")}</p>
      </section>
      <section className="card">
        <h2 className="flex items-center gap-2 text-lg font-extrabold"><Wallet size={18} />Carteira de créditos (simulação)</h2>
        {!me.features.wallet_enabled ? <p className="mt-2 flex items-center gap-2 text-sm text-[#64748B]"><Lock size={14} />Carteira desativada pelo administrador.</p>
          : p.status !== "aprovado" ? <p className="mt-2 text-sm text-[#64748B]">Disponível após aprovação.</p>
          : wallet && (
            <>
              <p className="mt-3 text-3xl font-extrabold" data-testid="lawyer-balance">{wallet.balance} créditos</p>
              <p className="text-xs text-[#64748B]">1 crédito = {brl(me.credit_unit_price)} (valor simulado). Nenhuma cobrança real é realizada.</p>
              <div className="mt-4 flex flex-wrap gap-2">{me.packages.map((k) => <button key={k.id} className="btn-secondary btn-sm" onClick={() => simulate(k.id)} data-testid={`simulate-pkg-${k.id}`}>{k.label}: {k.credits} cr · {brl(k.price)}</button>)}</div>
              <ul className="mt-4 space-y-1 text-sm">{wallet.transactions.map((t) => <li key={t.tx_id}>{fmtDateTime(t.created_at)} · {t.nature} · {t.amount > 0 ? "+" : ""}{t.amount} · {t.status}</li>)}</ul>
            </>
          )}
      </section>
      <section className="card"><h2 className="text-lg font-extrabold">Solicitações dirigidas a você</h2><p className="mt-2 flex items-center gap-2 text-sm text-[#64748B]"><Lock size={14} />Funcionalidade bloqueada até validação jurídica específica.</p></section>
    </div>
  );
}

export default function Advogado() {
  useSeo("Portal do advogado", null, true);
  const { user } = useAuth();
  const { settings } = useSettings() || {};
  return (
    <>
      <PageHead eyebrow="Expansão profissional" title="Portal do Advogado" />
      <div className="wrap mt-10 max-w-4xl">
        {user && user.role === "lawyer" ? <Portal /> : settings?.lawyer_signup_open ? <Register /> : (
          <p className="card flex items-center gap-3 text-[#64748B]" data-testid="lawyer-portal-disabled"><Lock />O cadastro de profissionais não está disponível no momento.</p>
        )}
      </div>
    </>
  );
}
